import { useTranslation } from '@repo/i18n'
import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import type { Key, ReactNode, Ref, RefObject } from 'react'
import {
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { VirtualList, type VirtualListProps } from '#commons/virtual-list'
import { Skeleton } from '#components/skeleton'
import type { VirtualizationApi } from '#hooks/use-virtualization'
import { cn } from '#lib/utils'

interface PageState {
	active: boolean
	fetching: boolean
	restore: {
		anchor: { key: string; top: number } | null
		key: string
		loaderHeight: number
		scrollHeight: number
		scrollTop: number
	} | null
	visible: { key: string; top: number } | null
}

export function InfiniteVirtualList<TItem>(
	props: Omit<
		VirtualListProps<TItem>,
		| 'axis'
		| 'children'
		| 'footer'
		| 'header'
		| 'items'
		| 'onEndReached'
		| 'onStartReached'
		| 'overscan'
		| 'ref'
		| 'threshold'
	> & {
		children: (item: TItem, index: number) => ReactNode
		empty?: ReactNode
		error?: ReactNode
		getItemKey?: (item: TItem, index: number) => Key
		header?: ReactNode
		items: TItem[]
		pagination?: 'start' | 'end' | false
		query: Pick<
			UseInfiniteQueryResult,
			| 'fetchNextPage'
			| 'hasNextPage'
			| 'isError'
			| 'isFetching'
			| 'isPending'
		>
		ref?: Ref<VirtualizationApi>
		skeleton?: ReactNode
	}
) {
	const listRef = useRef<VirtualizationApi>(null)
	const pageRef = useRef<PageState>({
		active: false,
		fetching: false,
		restore: null,
		visible: null
	})
	const [pageActive, setPageActive] = useState(false)
	const translation = useTranslation()
	const pagination = props.pagination ?? 'end'
	const getItemKey = props.getItemKey ?? defaultItemKey
	const items = props.items
	const canPage =
		pagination !== false && props.query.hasNextPage && !props.query.isError
	const pageLoader =
		pageActive && props.query.hasNextPage && !props.query.isError
	const pageError = props.query.isError && items.length > 0

	usePaging(
		listRef,
		pageRef,
		setPageActive,
		pageActive,
		pageLoader,
		pagination,
		items,
		getItemKey,
		props.query
	)

	useImperativeHandle(props.ref, () => ({
		getScrollElement: () => listRef.current?.getScrollElement() ?? null,
		getTotalSize: (axis) => listRef.current?.getTotalSize(axis) ?? 0,
		scrollToEnd: (axis, opts) => listRef.current?.scrollToEnd(axis, opts),
		scrollToIndex: (axis, index, opts) =>
			listRef.current?.scrollToIndex(axis, index, opts),
		scrollToOffset: (offset, opts) =>
			listRef.current?.scrollToOffset(offset, opts),
		scrollToStart: (axis, opts) =>
			listRef.current?.scrollToStart(axis, opts)
	}))

	const fallback = listFallback(
		props.query,
		items.length,
		props.empty,
		props.error,
		props.skeleton,
		translation.t
	)

	if (fallback !== undefined) {
		return fallback
	}

	const pageSlot =
		pageLoader || pageError ? (
			<div data-infinite-virtual-list-page-loader=''>
				{pageError
					? renderError(props.error, translation.t)
					: renderSkeleton(props.skeleton)}
			</div>
		) : null

	return (
		<VirtualList
			className={props.className}
			estimateSize={props.estimateSize}
			footer={pagination === 'end' && pageSlot}
			getItemKey={getItemKey}
			header={
				pagination === 'start' && pageSlot ? (
					<>
						{pageSlot}
						{props.header}
					</>
				) : (
					props.header
				)
			}
			initialOffset={props.initialOffset}
			items={items}
			onEndReached={
				pagination === 'end' && canPage && !pageActive
					? () =>
							requestPage(
								listRef,
								pageRef,
								setPageActive,
								pagination,
								items,
								getItemKey
							)
					: undefined
			}
			onStartReached={
				pagination === 'start' && canPage && !pageActive
					? () =>
							requestPage(
								listRef,
								pageRef,
								setPageActive,
								pagination,
								items,
								getItemKey
							)
					: undefined
			}
			ref={listRef}
			threshold={pagination === 'start' ? { px: 48 } : { items: 5 }}
		>
			{(item, index) => (
				<div
					data-infinite-virtual-item-key={String(
						getItemKey(item, index)
					)}
				>
					{props.children(item, index)}
				</div>
			)}
		</VirtualList>
	)
}

function usePaging<TItem>(
	listRef: RefObject<VirtualizationApi | null>,
	pageRef: RefObject<PageState>,
	setPageActive: (active: boolean) => void,
	pageActive: boolean,
	pageLoader: boolean,
	pagination: 'start' | 'end' | false,
	items: TItem[],
	getItemKey: (item: TItem, index: number) => Key,
	query: {
		fetchNextPage: (options?: {
			cancelRefetch?: boolean
		}) => Promise<unknown>
		hasNextPage: boolean
		isError: boolean
		isFetching: boolean
	}
) {
	useEffect(() => {
		async function fetchNextPage() {
			try {
				await query.fetchNextPage({ cancelRefetch: false })
			} catch {
				finishPage(pageRef, setPageActive)
			}
		}

		if (
			pageActive &&
			!pageRef.current.fetching &&
			query.hasNextPage &&
			!query.isError &&
			!query.isFetching
		) {
			pageRef.current.fetching = true
			fetchNextPage()
		}

		if (
			pageActive &&
			pageRef.current.fetching &&
			!query.isFetching &&
			!(query.hasNextPage && !query.isError && pageRef.current.restore)
		) {
			finishPage(pageRef, setPageActive)
		}
	}, [
		pageActive,
		pageRef,
		query.fetchNextPage,
		query.hasNextPage,
		query.isError,
		query.isFetching,
		setPageActive
	])

	useLayoutEffect(() => {
		const restore = pageRef.current.restore
		const key =
			pagination === false
				? null
				: getBoundaryKey(pagination, items, getItemKey)

		if (!(pageActive && restore && key && key !== restore.key)) {
			return
		}

		const list = listRef.current
		const scrollElement = list?.getScrollElement()

		if (!(list && scrollElement)) {
			finishPage(pageRef, setPageActive)
			return
		}

		const anchor = restore.anchor ?? pageRef.current.visible

		if (pagination !== 'start') {
			finishPage(pageRef, setPageActive)
			return
		}

		let secondFrame = 0
		const firstFrame = requestAnimationFrame(() => {
			secondFrame = requestAnimationFrame(() => {
				list.scrollToOffset(
					{
						vertical: Math.max(
							0,
							restore.scrollTop +
								scrollElement.scrollHeight -
								restore.scrollHeight -
								restore.loaderHeight
						)
					},
					{ align: 'start' }
				)
				finishPage(pageRef, setPageActive)

				if (anchor) {
					restoreAnchor(
						anchor,
						scrollElement,
						listRef,
						pageRef,
						items,
						getItemKey
					)
				}
			})
		})

		return () => {
			cancelAnimationFrame(firstFrame)

			if (secondFrame) {
				cancelAnimationFrame(secondFrame)
			}
		}
	}, [
		getItemKey,
		items,
		listRef,
		pageActive,
		pageRef,
		pagination,
		setPageActive
	])

	useLayoutEffect(() => {
		const restore = pageRef.current.restore
		const scrollElement = listRef.current?.getScrollElement()

		if (!(pageLoader && restore && scrollElement)) {
			return
		}

		const loader = scrollElement.querySelector<HTMLElement>(
			'[data-infinite-virtual-list-page-loader]'
		)

		restore.loaderHeight = loader?.getBoundingClientRect().height ?? 0
		restore.anchor = visibleAnchor(scrollElement) ?? restore.anchor
		pageRef.current.visible = restore.anchor ?? pageRef.current.visible

		const activeRestore = restore
		const activeScrollElement = scrollElement

		function updateLoadingAnchor() {
			activeRestore.anchor =
				visibleAnchor(activeScrollElement) ?? activeRestore.anchor
			pageRef.current.visible =
				activeRestore.anchor ?? pageRef.current.visible
		}

		activeScrollElement.addEventListener('scroll', updateLoadingAnchor, {
			passive: true
		})

		return () => {
			activeScrollElement.removeEventListener(
				'scroll',
				updateLoadingAnchor
			)
		}
	}, [listRef, pageLoader, pageRef])

	useLayoutEffect(() => {
		if (items.length === 0) {
			return
		}

		const scrollElement = listRef.current?.getScrollElement()

		if (!scrollElement) {
			return
		}

		const activeScrollElement = scrollElement

		function updateVisible() {
			if (!pageRef.current.active) {
				pageRef.current.visible =
					visibleAnchor(activeScrollElement) ??
					pageRef.current.visible
			}
		}

		updateVisible()
		activeScrollElement.addEventListener('scroll', updateVisible, {
			passive: true
		})

		return () => {
			activeScrollElement.removeEventListener('scroll', updateVisible)
		}
	}, [items.length, listRef, pageRef])

	useLayoutEffect(() => {
		const scrollElement = listRef.current?.getScrollElement()

		if (
			pagination === 'start' &&
			!pageActive &&
			query.hasNextPage &&
			!query.isError &&
			scrollElement &&
			scrollElement.scrollTop <= 48
		) {
			requestPage(
				listRef,
				pageRef,
				setPageActive,
				pagination,
				items,
				getItemKey
			)
		}
	}, [
		getItemKey,
		items,
		listRef,
		pageActive,
		pageRef,
		pagination,
		query.hasNextPage,
		query.isError,
		setPageActive
	])

	useLayoutEffect(() => {
		if (pageActive || items.length === 0) {
			return
		}

		const scrollElement = listRef.current?.getScrollElement()

		if (!scrollElement) {
			return
		}

		if (
			pageRef.current.visible &&
			pagination === 'start' &&
			!isAtEnd(scrollElement)
		) {
			shiftToAnchor(scrollElement, pageRef.current.visible)
		}

		pageRef.current.visible = visibleAnchor(scrollElement)
	}, [items, listRef, pageActive, pageRef, pagination])
}

function requestPage<TItem>(
	listRef: RefObject<VirtualizationApi | null>,
	pageRef: RefObject<PageState>,
	setPageActive: (active: boolean) => void,
	edge: 'start' | 'end',
	items: TItem[],
	getItemKey: (item: TItem, index: number) => Key
) {
	const page = pageRef.current
	const list = listRef.current
	const scrollElement = list?.getScrollElement()
	const key = getBoundaryKey(edge, items, getItemKey)

	if (page.active || !(list && scrollElement && key)) {
		return false
	}

	if (edge === 'start' && scrollElement.scrollTop > 48) {
		return false
	}

	const scrollTop = edge === 'start' ? 0 : scrollElement.scrollTop

	if (scrollTop !== scrollElement.scrollTop) {
		list.scrollToOffset({ vertical: scrollTop }, { align: 'start' })
	}

	page.active = true
	page.fetching = false
	page.restore = {
		anchor: visibleAnchor(scrollElement),
		key,
		loaderHeight: 0,
		scrollHeight: scrollElement.scrollHeight,
		scrollTop
	}
	setPageActive(true)
	return true
}

function finishPage(
	pageRef: RefObject<PageState>,
	setPageActive: (active: boolean) => void
) {
	pageRef.current.active = false
	pageRef.current.fetching = false
	pageRef.current.restore = null
	setPageActive(false)
}

function listFallback(
	query: {
		isError: boolean
		isPending: boolean
	},
	itemCount: number,
	empty: ReactNode,
	error: ReactNode,
	skeleton: ReactNode,
	t: ReturnType<typeof useTranslation>['t']
): ReactNode | undefined {
	// Only take over the whole list while there is nothing to show yet; items
	// sourced outside the query (e.g. a picker's pinned selections) stay visible
	// as a new page loads.
	if (query.isPending && itemCount === 0) {
		return renderSkeleton(skeleton)
	}

	if (query.isError && itemCount === 0) {
		return renderError(error, t)
	}

	if (itemCount === 0) {
		return empty === undefined ? (
			<div className='flex h-9 items-center justify-center text-muted-foreground text-sm'>
				{t('shared:infinite_virtual_list.empty')}
			</div>
		) : (
			empty
		)
	}
}

function defaultItemKey<TItem>(item: TItem, index: number): Key {
	if (typeof item !== 'object' || item === null) {
		return index
	}

	if ('id' in item && isKey(item.id)) {
		return item.id
	}

	if ('key' in item && isKey(item.key)) {
		return item.key
	}

	return index
}

function isKey(value: unknown): value is Key {
	return typeof value === 'string' || typeof value === 'number'
}

function getBoundaryKey<TItem>(
	edge: 'start' | 'end',
	items: TItem[],
	getItemKey: (item: TItem, index: number) => Key
) {
	const index = edge === 'start' ? 0 : items.length - 1
	const item = items[index]

	return item === undefined ? null : String(getItemKey(item, index))
}

function isAtEnd(scrollElement: HTMLElement) {
	return (
		scrollElement.scrollHeight -
			scrollElement.scrollTop -
			scrollElement.clientHeight <=
		48
	)
}

function visibleAnchor(scrollElement: HTMLElement) {
	const scrollTop = scrollElement.getBoundingClientRect().top

	for (const element of scrollElement.querySelectorAll<HTMLElement>(
		'[data-infinite-virtual-item-key]'
	)) {
		const key = element.dataset.infiniteVirtualItemKey
		const rect = element.getBoundingClientRect()

		if (key && rect.bottom > scrollTop) {
			return {
				key,
				top: rect.top - scrollTop
			}
		}
	}

	return null
}

function shiftToAnchor(
	scrollElement: HTMLElement,
	anchor: { key: string; top: number }
) {
	const scrollTop = scrollElement.getBoundingClientRect().top

	for (const element of scrollElement.querySelectorAll<HTMLElement>(
		'[data-infinite-virtual-item-key]'
	)) {
		if (element.dataset.infiniteVirtualItemKey !== anchor.key) {
			continue
		}

		const delta =
			element.getBoundingClientRect().top - scrollTop - anchor.top

		if (Math.abs(delta) > 0.5) {
			scrollElement.scrollTop += delta
		}

		return true
	}

	return false
}

function restoreAnchor<TItem>(
	anchor: { key: string; top: number },
	scrollElement: HTMLElement,
	listRef: RefObject<VirtualizationApi | null>,
	pageRef: RefObject<PageState>,
	items: TItem[],
	getItemKey: (item: TItem, index: number) => Key
) {
	let frame = 0

	function restore() {
		if (shiftToAnchor(scrollElement, anchor)) {
			pageRef.current.visible = visibleAnchor(scrollElement)
			return
		}

		const index = items.findIndex(
			(item, itemIndex) =>
				String(getItemKey(item, itemIndex)) === anchor.key
		)

		if (index !== -1 && !scrollToward(index, anchor, scrollElement)) {
			listRef.current?.scrollToIndex('vertical', index, {
				align: 'start'
			})
		}

		frame += 1

		if (frame < 20) {
			requestAnimationFrame(restore)
		}
	}

	restore()
}

function scrollToward(
	index: number,
	anchor: { key: string; top: number },
	scrollElement: HTMLElement
) {
	const scrollTop = scrollElement.getBoundingClientRect().top
	let count = 0
	let size = 0
	let nearestDistance = Number.POSITIVE_INFINITY
	let nearestIndex = 0
	let nearestTop = 0

	for (const element of scrollElement.querySelectorAll<HTMLElement>(
		'[data-index]'
	)) {
		const itemIndex = Number(element.dataset.index)
		const rect = element.getBoundingClientRect()

		if (!(Number.isFinite(itemIndex) && rect.height > 0)) {
			continue
		}

		const distance = Math.abs(index - itemIndex)
		count += 1
		size += rect.height

		if (distance < nearestDistance) {
			nearestDistance = distance
			nearestIndex = itemIndex
			nearestTop = rect.top - scrollTop
		}
	}

	if (count === 0) {
		return false
	}

	scrollElement.scrollTop +=
		nearestTop + (index - nearestIndex) * (size / count) - anchor.top

	return true
}

function InfiniteVirtualListSkeleton() {
	return (
		<div className='flex flex-col gap-2 p-2'>
			<Skeleton className='h-9' />
			<Skeleton className='h-9' />
			<Skeleton className='h-9' />
		</div>
	)
}

function renderSkeleton(skeleton: ReactNode) {
	return skeleton === undefined ? <InfiniteVirtualListSkeleton /> : skeleton
}

function renderError(
	slot: ReactNode,
	t: ReturnType<typeof useTranslation>['t']
) {
	return (
		slot ?? (
			<div className='flex h-9 items-center justify-center text-destructive text-sm'>
				{t('shared:infinite_virtual_list.error')}
			</div>
		)
	)
}

export function VirtualListSkeleton(props: {
	className?: string
	itemClassName?: string
	rows?: number
}) {
	return (
		<div
			className={cn(
				'flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-2',
				props.className
			)}
		>
			{Array.from(
				{ length: props.rows ?? 3 },
				(_, index) => `virtual-list-skeleton-${index}`
			).map((key) => (
				<Skeleton
					className={cn('h-9 rounded-md', props.itemClassName)}
					key={key}
				/>
			))}
		</div>
	)
}

export function VirtualListState(props: {
	children: ReactNode
	className?: string
	tone?: 'destructive' | 'muted'
}) {
	return (
		<div
			className={cn(
				'flex items-center justify-center text-center text-sm',
				props.tone === 'destructive'
					? 'text-destructive'
					: 'text-muted-foreground',
				props.className
			)}
		>
			{props.children}
		</div>
	)
}
